// Unified Notification API - supports both hook-based (for components) and promise-based (for Redux) usage
import {useCallback} from 'react';
import useHttp from '../../hooks/useHttp';

// Base API configuration
const API_BASE_URL = `${process.env.REACT_APP_IBILL_GATEWAY}/notification/api/v1` || 'http://localhost:8080/api/v1';

// Utility functions for promise-based API
const getAuthHeaders = () => {
  const token = localStorage.getItem('ibill_access_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

const handleResponse = async (response) => {
  const jsonResp = await response.json();

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${jsonResp.message || 'Request failed'}`);
  }

  // Handle both direct responses and ApiResponse wrapper format
  if (jsonResp.status === 'OK' || jsonResp.status === 'NO_CONTENT' || jsonResp.status === 'CREATED' ||
      (jsonResp.status && jsonResp.status.code === 200)) {
    return jsonResp.data || jsonResp;
  }

  if (response.ok && !jsonResp.status) {
    return jsonResp;
  }

  // Don't throw error for successful HTTP responses
  if (response.ok) {
    return jsonResp;
  }

  throw new Error(jsonResp.message || 'Request failed');
};

// ========================================
// HOOK-BASED API (for React Components)
// ========================================
const useNotificationApi = () => {
  const { isLoading, error, sendRequest } = useHttp();

  // Helper function to adapt useHttp behavior to notification callback format
  const createAdaptedCallback = (callback) => {
    return (data) => {
      // useHttp calls this when successful
      if (callback && typeof callback === 'function') {
        callback(data, null); // Success: (data, null)
      }
    };
  };

  // Monitor useHttp error state and call callbacks with error
  const createErrorAwareMethod = (apiCall, callback) => {
    const adaptedCallback = createAdaptedCallback(callback);

    // Call the API
    apiCall(adaptedCallback);

    // Check for errors after a brief delay
    setTimeout(() => {
      if (error && callback && typeof callback === 'function') {
        callback(null, new Error(error)); // Error: (null, error)
      }
    }, 100);
  };

  // Template Management APIs
  const getTemplates = useCallback((page = 0, size = 20, search, channel, callback) => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (search) params.append('search', search);
    if (channel) params.append('channel', channel);

    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/templates?${params}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const getTemplate = useCallback((id, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/templates/${id}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const createTemplate = useCallback((templateData, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/templates`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: templateData
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const updateTemplate = useCallback((id, templateData, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/templates/${id}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: templateData
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const deleteTemplate = useCallback((id, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/templates/${id}`,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const toggleTemplateStatus = useCallback((id, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/templates/${id}/toggle-status`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const validateTemplate = useCallback((templateData, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/templates/validate`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: templateData
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  // Template Variables APIs
  const getVariablesByTemplate = useCallback((templateId, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/template-variables/template/${templateId}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const createVariable = useCallback((variableData, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/template-variables`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: variableData
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const bulkUpdateVariables = useCallback((templateId, variables, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/template-variables/template/${templateId}/bulk`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: variables
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  // Email Configuration APIs
  const getEmailConfigs = useCallback((page = 0, size = 20, applicationName, callback) => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (applicationName) params.append('applicationName', applicationName);

    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/email-configs?${params}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const createEmailConfig = useCallback((configData, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/email-configs`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: configData
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const updateEmailConfig = useCallback((id, configData, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/email-configs/${id}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: configData
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const deleteEmailConfig = useCallback((id, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/email-configs/${id}`,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const testEmailConfig = useCallback((id, testEmail, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/email-configs/${id}/test?testEmailAddress=${testEmail}`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const toggleEmailConfigStatus = useCallback((id, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/email-configs/${id}/toggle-status`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  // SMS Configuration APIs
  const getSmsConfigs = useCallback((page = 0, size = 20, applicationName, gateway, callback) => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (applicationName) params.append('applicationName', applicationName);
    if (gateway) params.append('gateway', gateway);

    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/sms-configs?${params}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const createSmsConfig = useCallback((configData, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/sms-configs`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: configData
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const updateSmsConfig = useCallback((id, configData, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/sms-configs/${id}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: configData
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const deleteSmsConfig = useCallback((id, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/sms-configs/${id}`,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const testSmsConfig = useCallback((id, testPhoneNumber, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/sms-configs/${id}/test?testPhoneNumber=${testPhoneNumber}`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const toggleSmsConfigStatus = useCallback((id, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/sms-configs/${id}/toggle-status`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  // Push Configuration APIs
  const getPushConfigs = useCallback((page = 0, size = 20, applicationName, callback) => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (applicationName) params.append('applicationName', applicationName);

    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/push-configs?${params}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const createPushConfig = useCallback((configData, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/push-configs`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: configData
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const updatePushConfig = useCallback((id, configData, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/push-configs/${id}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: configData
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const deletePushConfig = useCallback((id, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/push-configs/${id}`,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const testPushConfig = useCallback((id, testDeviceToken, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/push-configs/${id}/test?testDeviceToken=${encodeURIComponent(testDeviceToken)}`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const togglePushConfigStatus = useCallback((id, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/push-configs/${id}/toggle-status`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const getAvailablePushProviders = useCallback((callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/push-configs/providers`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  // Notification Service APIs
  const sendNotification = useCallback((notificationData, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/send-message`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: notificationData
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  // Configuration Summary API
  const getConfigurationSummary = useCallback((callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/notification-config/summary`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const getAvailableGateways = useCallback((callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/sms-configs/gateways`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  // Template Routes APIs
  const getRoutesByTemplate = useCallback((templateId, channel, callback) => {
    const params = new URLSearchParams();
    if (channel) params.append('channel', channel);

    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/template-routes/template/${templateId}?${params}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const getAvailableRoutes = useCallback((channel, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/template-routes/available?channel=${channel}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const createRoute = useCallback((routeData, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/template-routes`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: routeData
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const updateRoute = useCallback((id, routeData, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/template-routes/${id}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: routeData
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const deleteRoute = useCallback((id, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/template-routes/${id}`,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  const toggleRouteStatus = useCallback((id, callback) => {
    const apiCall = (adaptedCallback) => {
      sendRequest({
        url: `${API_BASE_URL}/admin/template-routes/${id}/toggle-status`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      }, adaptedCallback);
    };

    createErrorAwareMethod(apiCall, callback);
  }, [sendRequest, error]);

  return {
    isLoading,
    error,
    // Template APIs
    getTemplates,
    getTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    toggleTemplateStatus,
    validateTemplate,
    // Template Variables APIs
    getVariablesByTemplate,
    createVariable,
    bulkUpdateVariables,
    // Email Config APIs
    getEmailConfigs,
    createEmailConfig,
    updateEmailConfig,
    deleteEmailConfig,
    testEmailConfig,
    toggleEmailConfigStatus,
    // SMS Config APIs
    getSmsConfigs,
    createSmsConfig,
    updateSmsConfig,
    deleteSmsConfig,
    testSmsConfig,
    toggleSmsConfigStatus,
    // Push Config APIs
    getPushConfigs,
    createPushConfig,
    updatePushConfig,
    deletePushConfig,
    testPushConfig,
    togglePushConfigStatus,
    getAvailablePushProviders,
    // Notification Service APIs
    sendNotification,
    // Configuration APIs
    getConfigurationSummary,
    getAvailableGateways,
    // Template Routes APIs
    getRoutesByTemplate,
    getAvailableRoutes,
    createRoute,
    updateRoute,
    deleteRoute,
    toggleRouteStatus,
  };
};

// ========================================
// PROMISE-BASED API (for Redux Actions)
// ========================================

// Template Management APIs
export const getTemplates = async (page = 0, size = 20, search, channel) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
  });

  if (search) params.append('search', search);
  if (channel) params.append('channel', channel);

  const response = await fetch(`${API_BASE_URL}/admin/templates?${params}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const createTemplate = async (templateData) => {
  const response = await fetch(`${API_BASE_URL}/admin/templates`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(templateData)
  });

  return handleResponse(response);
};

export const updateTemplate = async (id, templateData) => {
  const response = await fetch(`${API_BASE_URL}/admin/templates/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(templateData)
  });

  return handleResponse(response);
};

export const deleteTemplate = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/templates/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const toggleTemplateStatus = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/templates/${id}/toggle-status`, {
    method: 'PATCH',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

// Configuration Summary API
export const getConfigurationSummary = async () => {
  const response = await fetch(`${API_BASE_URL}/admin/notification-config/summary`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

// Email Configuration APIs
export const getEmailConfigs = async (page = 0, size = 20, applicationName) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
  });

  if (applicationName) params.append('applicationName', applicationName);

  const response = await fetch(`${API_BASE_URL}/admin/email-configs?${params}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const createEmailConfig = async (configData) => {
  const response = await fetch(`${API_BASE_URL}/admin/email-configs`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(configData)
  });

  return handleResponse(response);
};

export const updateEmailConfig = async (id, configData) => {
  const response = await fetch(`${API_BASE_URL}/admin/email-configs/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(configData)
  });

  return handleResponse(response);
};

export const deleteEmailConfig = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/email-configs/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const toggleEmailConfigStatus = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/email-configs/${id}/toggle-status`, {
    method: 'PATCH',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const testEmailConfig = async (id, testEmail) => {
  const response = await fetch(`${API_BASE_URL}/admin/email-configs/${id}/test?testEmailAddress=${testEmail}`, {
    method: 'POST',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

// SMS Configuration APIs
export const getSmsConfigs = async (page = 0, size = 20, applicationName, gateway) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
  });

  if (applicationName) params.append('applicationName', applicationName);
  if (gateway) params.append('gateway', gateway);

  const response = await fetch(`${API_BASE_URL}/admin/sms-configs?${params}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const createSmsConfig = async (configData) => {
  const response = await fetch(`${API_BASE_URL}/admin/sms-configs`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(configData)
  });

  return handleResponse(response);
};

export const updateSmsConfig = async (id, configData) => {
  const response = await fetch(`${API_BASE_URL}/admin/sms-configs/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(configData)
  });

  return handleResponse(response);
};

export const deleteSmsConfig = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/sms-configs/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const toggleSmsConfigStatus = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/sms-configs/${id}/toggle-status`, {
    method: 'PATCH',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const testSmsConfig = async (id, testPhone) => {
  const response = await fetch(`${API_BASE_URL}/admin/sms-configs/${id}/test?testPhoneNumber=${testPhone}`, {
    method: 'POST',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

// Push Configuration APIs
export const getPushConfigs = async (page = 0, size = 20, applicationName) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
  });

  if (applicationName) params.append('applicationName', applicationName);

  const response = await fetch(`${API_BASE_URL}/admin/push-configs?${params}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const createPushConfig = async (configData) => {
  const response = await fetch(`${API_BASE_URL}/admin/push-configs`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(configData)
  });

  return handleResponse(response);
};

export const updatePushConfig = async (id, configData) => {
  const response = await fetch(`${API_BASE_URL}/admin/push-configs/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(configData)
  });

  return handleResponse(response);
};

export const deletePushConfig = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/push-configs/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const togglePushConfigStatus = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/push-configs/${id}/toggle-status`, {
    method: 'PATCH',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const testPushConfig = async (id, testDeviceToken) => {
  const response = await fetch(`${API_BASE_URL}/admin/push-configs/${id}/test?testDeviceToken=${encodeURIComponent(testDeviceToken)}`, {
    method: 'POST',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

// Send Notification API
export const sendNotification = async (notificationData) => {
  const response = await fetch(`${API_BASE_URL}/send-message`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(notificationData)
  });

  return handleResponse(response);
};

export const getNotificationRequests = async (filters = {}) => {
  const params = new URLSearchParams();
  const {
    page = 0,
    size = 20,
    channel,
    status,
    customerId,
    recipient,
    requestId,
    batchRequestId,
    fromDate,
    toDate
  } = filters;

  params.append('page', page.toString());
  params.append('size', size.toString());

  if (channel) params.append('channel', channel);
  if (status) params.append('status', status);
  if (customerId) params.append('customerId', customerId);
  if (recipient) params.append('recipient', recipient);
  if (requestId) params.append('requestId', requestId);
  if (batchRequestId) params.append('batchRequestId', batchRequestId);
  if (fromDate) params.append('fromDate', fromDate);
  if (toDate) params.append('toDate', toDate);

  const response = await fetch(`${API_BASE_URL}/admin/notification-requests?${params.toString()}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const getNotificationRequestDetails = async (requestId) => {
  const response = await fetch(`${API_BASE_URL}/admin/notification-requests/${requestId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

// Template Route Management APIs
export const getRoutesByTemplate = async (templateId, channel) => {
  const params = new URLSearchParams();
  if (channel) params.append('channel', channel);

  const response = await fetch(`${API_BASE_URL}/admin/template-routes/template/${templateId}?${params}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const getAvailableRoutes = async (channel) => {
  const response = await fetch(`${API_BASE_URL}/admin/template-routes/available?channel=${channel}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const createRoute = async (routeData) => {
  const response = await fetch(`${API_BASE_URL}/admin/template-routes`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(routeData)
  });

  return handleResponse(response);
};

export const updateRoute = async (id, routeData) => {
  const response = await fetch(`${API_BASE_URL}/admin/template-routes/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(routeData)
  });

  return handleResponse(response);
};

export const deleteRoute = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/template-routes/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const toggleRouteStatus = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/template-routes/${id}/toggle-status`, {
    method: 'PATCH',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

// Template Variables APIs
export const getVariablesByTemplate = async (templateId) => {
  const response = await fetch(`${API_BASE_URL}/admin/template-variables/template/${templateId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

// Template Routes helper functions
export const getTemplateRoutes = async (templateId) => {
  const response = await fetch(`${API_BASE_URL}/admin/template-routes/template/${templateId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const updateTemplateRoutes = async (templateId, routes) => {
  const results = await Promise.all(
    routes.map(async (route) => {
      if (route.id) {
        return updateRoute(route.id, route);
      }
      const routeData = {
        ...route,
        templateId
      };
      return createRoute(routeData);
    })
  );
  return results;
};

// Default export for hook-based usage
export default useNotificationApi;
